import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import {
    Plus,
    Trash2,
    Edit2,
    X,
    Play,
    AlertCircle,
    Loader2
} from 'lucide-react';

interface PostbackConfig {
    id: string;
    url: string;
    event_type: 'signup' | 'purchase' | 'upsell' | 'all';
    method: 'GET' | 'POST';
    active: boolean;
}

export default function AffiliatePostbacks() {
    const [configs, setConfigs] = useState<PostbackConfig[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingConfig, setEditingConfig] = useState<PostbackConfig | null>(null);
    const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
    const [testingId, setTestingId] = useState<string | null>(null);

    // Form state
    const [formData, setFormData] = useState<Partial<PostbackConfig>>({
        url: '',
        event_type: 'all',
        method: 'GET',
        active: true
    });

    useEffect(() => {
        fetchConfigs();
    }, []);

    const fetchConfigs = async () => {
        setLoading(true);
        try {
            const managePostbackConfig = httpsCallable(functions, 'managePostbackConfig');
            const result = await managePostbackConfig({ action: 'list' });
            const data = result.data as any;
            if (data.success) {
                setConfigs(data.configs);
            }
        } catch (error) {
            console.error('Error fetching configs:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!formData.url) return;

        try {
            const managePostbackConfig = httpsCallable(functions, 'managePostbackConfig');
            const action = editingConfig ? 'update' : 'create';

            await managePostbackConfig({
                action,
                config: {
                    ...formData,
                    id: editingConfig?.id
                }
            });

            setShowModal(false);
            setEditingConfig(null);
            setFormData({ url: '', event_type: 'all', method: 'GET', active: true });
            fetchConfigs();
        } catch (error) {
            console.error('Error saving config:', error);
            alert('Failed to save configuration');
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Are you sure you want to delete this postback?')) return;

        try {
            const managePostbackConfig = httpsCallable(functions, 'managePostbackConfig');
            await managePostbackConfig({ action: 'delete', config: { id } });
            fetchConfigs();
        } catch (error) {
            console.error('Error deleting config:', error);
            alert('Failed to delete configuration');
        }
    };

    const handleTest = async (config: PostbackConfig) => {
        setTestingId(config.id);
        setTestResult(null);
        try {
            const managePostbackConfig = httpsCallable(functions, 'managePostbackConfig');
            const result = await managePostbackConfig({ action: 'test', config: { url: config.url, method: config.method } });
            const data = result.data as any;
            setTestResult({
                success: data.success,
                message: data.message
            });
        } catch (error: any) {
            setTestResult({
                success: false,
                message: error.message || 'Test failed'
            });
        } finally {
            setTestingId(null);
        }
    };

    const openEdit = (config: PostbackConfig) => {
        setEditingConfig(config);
        setFormData({
            url: config.url,
            event_type: config.event_type,
            method: config.method,
            active: config.active
        });
        setShowModal(true);
    };

    const openCreate = () => {
        setEditingConfig(null);
        setFormData({ url: '', event_type: 'all', method: 'GET', active: true });
        setShowModal(true);
    };

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-white">Postback Management</h1>
                    <p className="text-gray-400">Configure server-to-server postbacks for your conversions</p>
                </div>
                <button
                    onClick={openCreate}
                    className="btn-primary flex items-center gap-2"
                >
                    <Plus size={20} />
                    New Postback
                </button>
            </div>

            {/* Test Result Alert */}
            {testResult && (
                <div className={`p-4 rounded-lg border flex items-start gap-3 ${testResult.success ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border-red-500/20 text-red-400'
                    }`}>
                    <AlertCircle size={20} className="mt-0.5" />
                    <div>
                        <p className="font-medium">{testResult.success ? 'Test Successful' : 'Test Failed'}</p>
                        <p className="text-sm opacity-90">{testResult.message}</p>
                    </div>
                    <button onClick={() => setTestResult(null)} className="ml-auto hover:text-white">
                        <X size={16} />
                    </button>
                </div>
            )}

            {/* Configs List */}
            <div className="card">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="border-b border-white/10">
                                <th className="py-3 px-4 text-gray-400 font-medium">Method</th>
                                <th className="py-3 px-4 text-gray-400 font-medium w-full">URL</th>
                                <th className="py-3 px-4 text-gray-400 font-medium">Event</th>
                                <th className="py-3 px-4 text-gray-400 font-medium">Status</th>
                                <th className="py-3 px-4 text-gray-400 font-medium text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={5} className="py-8 text-center">
                                        <Loader2 size={24} className="mx-auto animate-spin text-primary-500" />
                                    </td>
                                </tr>
                            ) : configs.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="py-8 text-center text-gray-500">
                                        No postbacks configured yet.
                                    </td>
                                </tr>
                            ) : (
                                configs.map(config => (
                                    <tr key={config.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                                        <td className="py-4 px-4">
                                            <span className={`px-2 py-1 rounded text-xs font-bold ${config.method === 'POST' ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'
                                                }`}>
                                                {config.method}
                                            </span>
                                        </td>
                                        <td className="py-4 px-4">
                                            <div className="font-mono text-sm text-gray-300 break-all max-w-lg">
                                                {config.url}
                                            </div>
                                        </td>
                                        <td className="py-4 px-4">
                                            <span className="capitalize text-gray-300">{config.event_type}</span>
                                        </td>
                                        <td className="py-4 px-4">
                                            {config.active ? (
                                                <span className="flex items-center gap-1.5 text-emerald-400 text-sm">
                                                    <span className="w-2 h-2 rounded-full bg-emerald-400" />
                                                    Active
                                                </span>
                                            ) : (
                                                <span className="flex items-center gap-1.5 text-gray-500 text-sm">
                                                    <span className="w-2 h-2 rounded-full bg-gray-500" />
                                                    Inactive
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-4 px-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => handleTest(config)}
                                                    disabled={testingId === config.id}
                                                    title="Test Fire"
                                                    className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors disabled:opacity-50"
                                                >
                                                    {testingId === config.id ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
                                                </button>
                                                <button
                                                    onClick={() => openEdit(config)}
                                                    className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                                                >
                                                    <Edit2 size={16} />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(config.id)}
                                                    className="p-2 rounded-lg bg-white/5 hover:bg-red-500/20 text-gray-400 hover:text-red-400 transition-colors"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Edit/Create Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <div className="card w-full max-w-lg relative animate-slide-up">
                        <button
                            onClick={() => setShowModal(false)}
                            className="absolute top-4 right-4 text-gray-400 hover:text-white"
                        >
                            <X size={20} />
                        </button>

                        <h2 className="text-xl font-bold text-white mb-6">
                            {editingConfig ? 'Edit Postback' : 'New Postback'}
                        </h2>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-1">Postback URL</label>
                                <input
                                    type="text"
                                    value={formData.url}
                                    onChange={e => setFormData({ ...formData, url: e.target.value })}
                                    className="input-field w-full"
                                    placeholder="https://tracker.com/pb?click={click_id}&payout={payout}"
                                />
                                <p className="text-xs text-gray-500 mt-2">
                                    Available placeholders: <span className="text-primary-400">{`{click_id}, {amount}, {payout}, {currency}, {conversion_id}, {event_type}, {sub_id}, {source_id}`}</span>
                                </p>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-1">Method</label>
                                    <select
                                        value={formData.method}
                                        onChange={e => setFormData({ ...formData, method: e.target.value as any })}
                                        className="input-field w-full"
                                    >
                                        <option value="GET">GET</option>
                                        <option value="POST">POST</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-1">Event Type</label>
                                    <select
                                        value={formData.event_type}
                                        onChange={e => setFormData({ ...formData, event_type: e.target.value as any })}
                                        className="input-field w-full"
                                    >
                                        <option value="all">All Events</option>
                                        <option value="purchase">Purchase (Default)</option>
                                        <option value="signup">Lead / Signup</option>
                                        <option value="upsell">Upsell</option>
                                    </select>
                                </div>
                            </div>

                            <div className="flex items-center gap-3 pt-2">
                                <label className="flex items-center gap-2 text-gray-300 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.active}
                                        onChange={e => setFormData({ ...formData, active: e.target.checked })}
                                        className="rounded border-gray-600 bg-black/50 text-primary-500 focus:ring-primary-500"
                                    />
                                    Active
                                </label>
                            </div>

                            <div className="pt-4 flex justify-end gap-3">
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSave}
                                    className="btn-primary px-6"
                                    disabled={!formData.url}
                                >
                                    Save Config
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
